// Configuración de rangos de clasificación de bodegas por ventas (letras A–F…).
// Inventario → Bodegas → Clasificación de bodegas.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Loader2, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, ClasificacionVenta, Modal, Switch, fieldCls, inv, invRpc, money, toNum } from './shared';

const vacio = { letra: '', descripcion: '', monto_desde: 0, monto_hasta: '', orden: 0, es_activo: true };

export function ClasificacionVentas() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<ClasificacionVenta[]>({
    queryKey: ['inv-clasif'], queryFn: () => inv.list<ClasificacionVenta>('clasificacion_ventas', '?select=*&order=orden,letra'),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (c?: ClasificacionVenta) => {
    if (c) { setEditId(c.id); setForm({ letra: c.letra, descripcion: c.descripcion ?? '', monto_desde: c.monto_desde, monto_hasta: c.monto_hasta != null ? String(c.monto_hasta) : '', orden: c.orden, es_activo: c.es_activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      const body = {
        letra: form.letra.toUpperCase(), descripcion: form.descripcion || null,
        monto_desde: Number(form.monto_desde) || 0,
        monto_hasta: form.monto_hasta === '' ? null : Number(form.monto_hasta),
        orden: Number(form.orden) || 0, es_activo: form.es_activo,
      };
      return editId ? inv.update('clasificacion_ventas', editId, body) : inv.create('clasificacion_ventas', body);
    },
    onSuccess: () => { toast.success('Rango guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['inv-clasif'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.remove('clasificacion_ventas', id),
    onSuccess: () => { toast.success('Rango eliminado'); void qc.invalidateQueries({ queryKey: ['inv-clasif'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const reclasificar = useMutation({
    mutationFn: () => invRpc<{ clasificadas: number }>('inv_clasificar_bodegas', {}),
    onSuccess: (r) => { toast.success(`${r.clasificadas} bodega(s) reclasificada(s)`); void qc.invalidateQueries({ queryKey: ['inv-locations'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Clasificación de bodegas por ventas</h1>
        <div className="flex gap-2">
          <Button variant="outline" isLoading={reclasificar.isPending} onClick={async () => { if (await dlg.confirmar('¿Reclasificar todas las bodegas con sus ventas actuales y estos rangos?')) reclasificar.mutate(); }}>
            <RefreshCw className="w-4 h-4 mr-1" /> Reclasificar ahora
          </Button>
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo rango</Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        La clasificación se basa en las ventas de los <span className="font-medium">últimos 12 meses</span> de cada bodega.
        El proceso nocturno asignará la letra automáticamente; «Reclasificar ahora» permite recalcular bajo demanda.
      </p>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Rangos ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Orden</th><th className="py-2 pr-3">Letra</th><th className="py-2 pr-3">Descripción</th>
              <th className="py-2 pr-3 text-right">Monto desde</th><th className="py-2 pr-3 text-right">Monto hasta</th>
              <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin rangos definidos</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{c.orden}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">{c.letra}</span></td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.descripcion ?? '—'}</td>
                  <td className="py-2 pr-3 text-right">{money(c.monto_desde)}</td>
                  <td className="py-2 pr-3 text-right">{c.monto_hasta != null ? money(c.monto_hasta) : 'sin tope'}</td>
                  <td className="py-2 pr-3">{c.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(c)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar el rango "${c.letra}"?`, { peligro: true })) eliminar.mutate(c.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar rango' : 'Nuevo rango'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Letra *"><input className={fieldCls} maxLength={2} value={form.letra} onChange={(e) => set({ letra: e.target.value.toUpperCase() })} /></Campo>
              <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => set({ orden: toNum(e.target.value) })} /></Campo>
            </div>
            <Campo label="Descripción"><input className={fieldCls} value={form.descripcion} onChange={(e) => set({ descripcion: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Monto desde"><input type="number" step="0.01" min="0" className={fieldCls} value={form.monto_desde} onChange={(e) => set({ monto_desde: toNum(e.target.value) })} /></Campo>
              <Campo label="Monto hasta (vacío = sin tope)"><input type="number" step="0.01" className={fieldCls} value={form.monto_hasta} onChange={(e) => set({ monto_hasta: e.target.value })} /></Campo>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activo" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.letra.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
